import { combinatoricsDeleteScenarios } from 'zycommon-web/utils/symbolic-tools/combinatorics/combinatorics-math-live';
import { negationInsertScenarios } from 'zycommon-web/utils/symbolic-tools/common-number';
import { functionsDeleteScenarios, functionsInsertScenarios } from 'zycommon-web/utils/symbolic-tools/functions/functions-math-live';
import {
    getNumberAndUnitDeleteScenarios,
    getNumberAndUnitInsertScenarios,
} from 'zycommon-web/utils/symbolic-tools/number-and-units/number-and-units-math-live';
import {
    numericVariablesDeleteScenarios,
    numericVariablesInsertScenarios,
} from 'zycommon-web/utils/symbolic-tools/numeric-variables/numeric-variables-math-live';
import {
    getSymbolicNumberDeleteScenarios,
    getSymbolicNumberInsertScenarios,
} from 'zycommon-web/utils/symbolic-tools/symbolic-number/symbolic-number-math-live';

import { boolFromUnknown } from '@/lib/primitive-utils';
import type { BuilderPropertyRecord } from '../tool-settings';

import { resolveNotation, resolveRequireUnits } from '../tool-settings/lib/builder-value-utils';

export type MathliveScenarioEditor = {
    value: string;
    position: number;
    lastOffset: number;
    executeCommand: (command: string | string[]) => void;
};

export type MathliveScenario = {
    isScenarioPresent: (value: string, previousUserAnswer?: string) => boolean;
    handleScenario: (
        editor: MathliveScenarioEditor,
        previousValue?: string,
        previousCaretWasAtEnd?: boolean,
        expectedAnswer?: string,
    ) => void;
};

export type ScenarioQuestionContext = Record<string, unknown> & {
    notation?: string;
    require_units?: boolean;
    accept_E_notation?: boolean;
    accept_scientific_notation?: boolean;
    require_strict_scientific_notation?: boolean;
    supportScientificNotation?: boolean;
    expectUnitless?: boolean;
    unitless_option?: string;
};

export function builderPropsToScenarioContext(builder: BuilderPropertyRecord): ScenarioQuestionContext {
    const acceptScientificNotation = boolFromUnknown(builder.accept_scientific_notation);
    const requireStrictScientificNotation = boolFromUnknown(builder.require_strict_scientific_notation);
    const unitlessOption = typeof builder.unitless_option === 'string' ? builder.unitless_option : '';

    return {
        ...builder,
        accept_E_notation: boolFromUnknown(builder.accept_E_notation),
        accept_scientific_notation: acceptScientificNotation,
        require_strict_scientific_notation: requireStrictScientificNotation,
        require_units: boolFromUnknown(builder.require_units),
        supportScientificNotation: acceptScientificNotation || requireStrictScientificNotation,
        expectUnitless: unitlessOption.startsWith('Unitless'),
    };
}

/**
 * Same branch order as `deleteScenarios` in zb-math-live.js.
 */
export function getDeleteScenariosForShortAnswer(builder: BuilderPropertyRecord): MathliveScenario[] {
    const notation = resolveNotation(builder);
    const requireUnits = resolveRequireUnits(builder);
    const question = builderPropsToScenarioContext(builder);
    const deleteScenarios: MathliveScenario[] = [];

    if (notation === 'combinatorics') {
        deleteScenarios.push(...combinatoricsDeleteScenarios);
    } else if (notation === 'functions') {
        deleteScenarios.push(...functionsDeleteScenarios);
    } else if (notation === 'number and units' || requireUnits) {
        deleteScenarios.push(...getNumberAndUnitDeleteScenarios(question));
    } else if (notation === 'numeric variables' || notation === 'symbolic equivalence') {
        deleteScenarios.push(...numericVariablesDeleteScenarios);
    } else {
        deleteScenarios.push(...getSymbolicNumberDeleteScenarios(question));
    }

    return deleteScenarios;
}

/**
 * Same branch order as `insertScenarios` in zb-math-live.js.
 */
export function getInsertScenariosForShortAnswer(builder: BuilderPropertyRecord): MathliveScenario[] {
    const notation = resolveNotation(builder);
    const requireUnits = resolveRequireUnits(builder);
    const question = builderPropsToScenarioContext(builder);
    const insertScenarios: MathliveScenario[] = [...negationInsertScenarios];

    if (notation === 'functions') {
        insertScenarios.push(...functionsInsertScenarios);
    } else if (notation === 'number and units' || requireUnits) {
        insertScenarios.push(...getNumberAndUnitInsertScenarios(question));
    } else if (notation === 'numeric variables' || notation === 'symbolic equivalence') {
        insertScenarios.push(...numericVariablesInsertScenarios);
    } else {
        insertScenarios.push(...getSymbolicNumberInsertScenarios(question));
    }

    return insertScenarios;
}

export const MATHLIVE_DELETE_INPUT_TYPES = [
    'deleteByCut',
    'deleteContent',
    'deleteContentBackward',
    'deleteContentForward',
    'deleteWordBackward',
    'deleteWordForward',
] as const;

export const MATHLIVE_INSERT_INPUT_TYPES = ['insertText', 'insertFromPaste'] as const;

export function isMathliveDeleteInputType(inputType: string): boolean {
    return (MATHLIVE_DELETE_INPUT_TYPES as readonly string[]).includes(inputType);
}

export function isMathliveInsertInputType(inputType: string): boolean {
    return (MATHLIVE_INSERT_INPUT_TYPES as readonly string[]).includes(inputType);
}

export function runMatchingMathliveScenario(
    scenarios: MathliveScenario[],
    editor: MathliveScenarioEditor,
    previousUserAnswer: string,
    previousCaretWasAtEnd: boolean,
    expectedString: string,
): void {
    scenarios
        .find(({ isScenarioPresent }) => isScenarioPresent(editor.value, previousUserAnswer))
        ?.handleScenario(editor, previousUserAnswer, previousCaretWasAtEnd, expectedString);
}
